import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiClient, Brand, BrandRating } from '../../services/api';

interface BrandState {
  brands: Brand[];
  currentBrand: Brand | null;
  currentRating: BrandRating | null;
  loading: boolean;
  error: string | null;
  ratingError: string | null;
  searchQuery: string;
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}

const initialState: BrandState = {
  brands: [],
  currentBrand: null,
  currentRating: null,
  loading: false,
  error: null,
  ratingError: null,
  searchQuery: '',
  pagination: {
    limit: 20,
    offset: 0,
    total: 0,
  },
};

// 异步actions
export const fetchBrands = createAsyncThunk(
  'brands/fetchBrands',
  async (params?: {
    search?: string;
    category?: string;
    startsWith?: string;
    limit?: number;
    offset?: number;
  }) => {
    const response = await apiClient.getBrands(params);
    return response;
  }
);

export const fetchBrand = createAsyncThunk(
  'brands/fetchBrand',
  async (id: number) => {
    const response = await apiClient.getBrand(id);
    return response.data;
  }
);

export const fetchBrandRating = createAsyncThunk(
  'brands/fetchBrandRating',
  async (brandId: number) => {
    const response = await apiClient.getBrandRating(brandId);
    return response.data;
  }
);

const brandSlice = createSlice({
  name: 'brands',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
    clearCurrentBrand: state => {
      state.currentBrand = null;
      state.currentRating = null;
      state.ratingError = null;
    },
  },
  extraReducers: builder => {
    builder
      // fetchBrands
      .addCase(fetchBrands.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBrands.fulfilled, (state, action) => {
        state.loading = false;
        state.brands = action.payload.data;
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchBrands.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch brands';
      })
      // fetchBrand
      .addCase(fetchBrand.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBrand.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBrand = action.payload;
      })
      .addCase(fetchBrand.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch brand';
      })
      // fetchBrandRating
      .addCase(fetchBrandRating.fulfilled, (state, action) => {
        state.currentRating = action.payload;
        state.ratingError = null;
      })
      .addCase(fetchBrandRating.rejected, (state, action) => {
        // 不影响整页加载状态，仅记录评分加载错误
        state.currentRating = null;
        state.ratingError =
          action.error.message || 'Failed to fetch brand rating';
      });
  },
});

export const { setSearchQuery, clearError, clearCurrentBrand } =
  brandSlice.actions;
export default brandSlice.reducer;
